import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem,
} from '@mint-lib/ag-grid/community';
import type { QueryClient } from '@mint-lib/query-client';
import { Auth } from '@mint-sdk/api-auth';

import { USER_TABLE_V2_CACHE_BLOCK_SIZE } from '../../constants/userTableV2.js';

const BACKEND_ORDERING_FIELD_BY_COLUMN_ID: Record<string, string> = {
  fullName: 'full_name',
  roleName: 'role_name',
};

export type UserTableV2Row = Auth.UserItem;

type UsersQueryClient = Pick<QueryClient, 'fetchQuery'>;

type CreateUsersDatasourceParams = {
  search?: string;
  queryClient: UsersQueryClient;
  onUsersCount?: (count: number) => void;
};

const getBackendOrderingField = (colId: string) =>
  BACKEND_ORDERING_FIELD_BY_COLUMN_ID[colId] ?? colId;

export const getUsersOrdering = (
  sortModel: SortModelItem[] = [],
): string | undefined => {
  if (!sortModel.length) return undefined;
  const { colId, sort } = sortModel[0];
  const orderingField = getBackendOrderingField(colId);
  return sort === 'desc' ? `-${orderingField}` : orderingField;
};

export const getUsersPagination = ({
  startRow = 0,
  endRow = 0,
}: Pick<IServerSideGetRowsParams['request'], 'startRow' | 'endRow'>) => ({
  offset: startRow,
  limit: endRow > startRow ? endRow - startRow : USER_TABLE_V2_CACHE_BLOCK_SIZE,
});

const loadUsers = async (
  params: IServerSideGetRowsParams<UserTableV2Row>,
  { search, queryClient, onUsersCount }: CreateUsersDatasourceParams,
) => {
  const { offset, limit } = getUsersPagination(params.request);

  try {
    const data = await queryClient.fetchQuery(
      Auth.listPaginatedUsers({
        params: {
          search: search || undefined,
          limit,
          offset,
          ordering: getUsersOrdering(params.request.sortModel),
          includeInheritedTags: false,
          // Labels come from the users_tags endpoint instead.
          excludeTags: true,
        },
      }),
    );

    if (params.api.isDestroyed()) {
      return;
    }

    if (data.count === 0) {
      params.api.showNoRowsOverlay();
    } else {
      params.api.hideOverlay();
    }

    params.success({ rowData: data.results, rowCount: data.count });
    onUsersCount?.(data.count);
  } catch {
    if (params.api.isDestroyed()) {
      return;
    }

    params.fail();
  }
};

export const createUsersDatasource = (
  options: CreateUsersDatasourceParams,
): IServerSideDatasource<UserTableV2Row> => ({
  getRows: (params) => {
    void loadUsers(params, options);
  },
});
